import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import React, { useState } from 'react';
import {
  Badge,
  Button,
  ButtonProps,
  Card,
  Col,
  Container,
  Form,
  OverlayTrigger,
  Row,
  Table,
  Tooltip
} from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { Option } from 'react-bootstrap-typeahead/types/types';
import { Link } from 'react-router-dom';

import { getCustomers, getEmptiesHistory, getProducts } from '../../apis/queries';
import EmptyTableState from '../../components/EmptyState/EmptyStateTable';
import Loader from '../../components/Loader';
import getStatusColor from '../../helpers/getStatusColor';
import { Customer, EmptiesHistory as EmptiesHistoryType, Product } from '../../types';
import { formatDateShort } from '../../utils/formatShortDate';
import { GET_CUSTOMERS_KEY } from '../Customers/Customers';
import { PRODUCTS_QUERY_KEY } from '../Products/Products';

interface BackButtonProps extends ButtonProps {
  onClick: () => void;
  label?: string;
}

export const EMPTIES_HISTORY_QUERY_KEY = 'emptiesHistory';

const BackButton: React.FC<BackButtonProps> = ({ onClick, label = 'Back', ...rest }) => {
  return (
    <Row className="justify-content-center mt-4">
      <Col xs={12} md={8} lg={6} className="text-center">
        <Button variant="primary" onClick={onClick} {...rest}>
          {label}
        </Button>
      </Col>
    </Row>
  );
};

const columnHelper = createColumnHelper<EmptiesHistoryType>();

const columns = [
  columnHelper.accessor('customer.name', {
    header: 'Customer Name',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('product.name', {
    header: 'Product Name',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('quantity', {
    header: 'Quantity',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('type', {
    header: 'Type',
    cell: info => <Badge bg={`${getStatusColor(info.getValue())}-soft`}>{info.getValue()}</Badge>,
    footer: info => info.column.id
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created',
    cell: info => formatDateShort(info.getValue()),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('drivers', {
    header: 'Drivers',
    cell: info => {
      const drivers = info.getValue() ?? [];
      const driverNames = drivers.map(driver => driver.name);
      return (
        <OverlayTrigger placement="top" overlay={drivers.length ? <Tooltip>{driverNames}</Tooltip> : <></>}>
          <span>{drivers.length}</span>
        </OverlayTrigger>
      );
    },
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('orderId', {
    header: 'Order',
    cell: info => (info.getValue() ? <Link to={`/order/${info.getValue()}`}>View Order</Link> : null),
    footer: info => info.column.id ?? ''
  })
];

const EmptiesHistory = () => {
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [productSelections, setProductSelections] = useState<Option[]>([]);
  const [customerSelections, setCustomerSelections] = useState<Option[]>([]);

  const selectProduct = (option: Option[]) => {
    setProductSelections(option);
    setCustomerSelections([]);
  };

  const selectCustomer = (option: Option[]) => {
    setCustomerSelections(option);
    setProductSelections([]);
  };

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
    setCustomerSelections([]);
    setProductSelections([]);
  };

  const handleBackClick = () => {
    setSelectedOption('');
    setCustomerSelections([]);
    setProductSelections([]);
  };

  return (
    <div className="main-content">
      <Container fluid="lg">
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8} xl={6} className="text-center">
            <h1 className="mb-3 mt-6">Empties Reconciliation Records</h1>
            <p className="mb-5 text-muted">Record of all empties both customers and products</p>
          </Col>
        </Row>
        {selectedOption === 'product' && productSelections.length ? (
          <EmptyHistoryTableView
            entity="product"
            onBackClick={handleBackClick}
            selectedParam={productSelections[0] as Product}
          />
        ) : selectedOption === 'customer' && customerSelections.length ? (
          <EmptyHistoryTableView
            entity="customer"
            onBackClick={handleBackClick}
            selectedParam={customerSelections[0] as Customer}
          />
        ) : (
          <>
            <Row className="justify-content-center">
              <Col xs={12} md={8} lg={6}>
                <Form.Group>
                  <Form.Label>Get Empties History By:</Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedOption}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOptionChange(e)}
                  >
                    <option value="">Select...</option>
                    <option value="customer">Customer</option>
                    <option value="product">Product</option>
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            {selectedOption && selectedOption === 'product' && <ProductTypeahead selectProduct={selectProduct} />}
            {selectedOption && selectedOption === 'customer' && <CustomerTypeahead selectCustomer={selectCustomer} />}
          </>
        )}
      </Container>
    </div>
  );
};

interface ProductTypeaheadProps {
  selectProduct: (option: Option[]) => void;
}

const ProductTypeahead = ({ selectProduct }: ProductTypeaheadProps) => {
  const compositeProductsQueryKey = [PRODUCTS_QUERY_KEY];

  const { data: productsData, isLoading: isLoadingProducts } = useQuery({
    queryKey: compositeProductsQueryKey,
    queryFn: () => getProducts()
  });

  if (isLoadingProducts) {
    return (
      <div className="justify-content-center mt-5">
        <Loader />
      </div>
    );
  }

  return (
    <Row className="justify-content-center mt-5">
      <Col xs={12} md={8} lg={6}>
        <Form.Group>
          <Form.Label>{'Product Name'}</Form.Label>
          <Typeahead
            id={`product-search`}
            onChange={selection => selectProduct(selection)}
            // selected={selections}
            disabled={isLoadingProducts}
            placeholder={`Select a product...`}
            options={productsData?.results ?? []}
            labelKey="name"
            minLength={2}
          />
        </Form.Group>
      </Col>
    </Row>
  );
};

interface CustomerTypeaheadProps {
  selectCustomer: (option: Option[]) => void;
}

const CustomerTypeahead = ({ selectCustomer }: CustomerTypeaheadProps) => {
  const compositeProductsQueryKey = [GET_CUSTOMERS_KEY];

  const { data: customersData, isLoading: isLoadingCustomers } = useQuery({
    queryKey: compositeProductsQueryKey,
    queryFn: () => getCustomers({ limit: 10000 })
  });

  if (isLoadingCustomers) {
    return (
      <div className="justify-content-center mt-5">
        <Loader />
      </div>
    );
  }

  return (
    <Row className="justify-content-center mt-5">
      <Col xs={12} md={8} lg={6}>
        <Form.Group>
          <Form.Label>{'Customer Name'}</Form.Label>
          <Typeahead
            id={`customer-search`}
            onChange={selection => selectCustomer(selection)}
            disabled={isLoadingCustomers}
            // selected={selections}
            placeholder={`Select a customer...`}
            options={customersData?.results ?? []}
            labelKey="name"
            minLength={2}
          />
        </Form.Group>
      </Col>
    </Row>
  );
};

interface EmptyHistoryTableViewProps {
  selectedParam: Product | Customer;
  entity: 'product' | 'customer';
  onBackClick: () => void;
}

const EmptyHistoryTableView = ({ selectedParam, entity, onBackClick }: EmptyHistoryTableViewProps) => {
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(undefined);

  const queryField = entity === 'product' ? 'productId' : 'customerId';

  const compositeEmptiesHistoryQueryKey = [EMPTIES_HISTORY_QUERY_KEY, { id: selectedParam._id }];

  const { data, isLoading } = useQuery({
    queryKey: compositeEmptiesHistoryQueryKey,
    queryFn: () => getEmptiesHistory({ [queryField]: selectedParam._id })
  });

  const tableInstance = useReactTable({
    data: data?.results ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter
  });

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <Loader />
      </div>
    );
  }

  if (!isLoading && data?.results?.length === 0) {
    return (
      <div>
        <EmptyTableState message="No empties history available." />
        <BackButton onClick={onBackClick} variant="primary" label="Back" style={{ width: '110px', height: '40px' }} />
      </div>
    );
  }

  return (
    <>
      <Row className="justify-content-center mt-4">
        <Col xs={12} md={10}>
          <Card>
            <Table size="sm" className="card-table table-nowrap" hover responsive>
              <thead>
                {tableInstance.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th colSpan={header.colSpan} key={header.id}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {tableInstance.getRowModel().rows.map(row => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>
      <BackButton onClick={onBackClick} variant="primary" label="Back" style={{ width: '110px', height: '40px' }} />
    </>
  );
};

export default EmptiesHistory;
